import { CheckIcon } from '@diligentcorp/atlas-react-icons';
import { CreateIssueResponseDto } from '@issuemanager/api-client';
import { ErrorFloatingCard } from '@issuemanager/apps/client/components';
import {
  APP_ERROR_TYPE,
  useAppErrorHandler,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { paths } from '@issuemanager/apps/client/paths';
import { Box, useEventCallback } from '@mui/material';
import { JSX, useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IssueFormFields } from '../components/issue-form-fields';
import { useSourceParams } from '../hooks/use-source-params';
import { useCreateIssueForm } from '../hooks/useCreateIssueForm';
import { generateCallbackUrl } from './utils';

export function CreateIssue(): JSX.Element {
  const { t } = useTranslation(['common', 'issue']);
  const navigate = useNavigate();
  const { setError } = useAppErrorHandler();
  const { setHeaderProps } = useHeaderStore();

  const { sourceInfo, isSourceError, isSourceExpected } = useSourceParams();

  const [isCancelling, setIsCancelling] = useState(false);

  const onCreated = useCallback(
    (issue: CreateIssueResponseDto) => {
      if (isSourceExpected && sourceInfo?.callbackUrl) {
        window.location.href = generateCallbackUrl({
          url: sourceInfo.callbackUrl,
          issueId: issue.code,
          result: 'success',
        });
      } else {
        navigate(`/${paths.issues.master}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSourceExpected, sourceInfo?.callbackUrl],
  );
  const onError = useCallback(() => {
    if (isSourceExpected && sourceInfo?.callbackUrl) {
      window.location.href = generateCallbackUrl({
        url: sourceInfo?.callbackUrl,
        result: 'error',
      });
    }
  }, [isSourceExpected, sourceInfo?.callbackUrl]);

  const {
    form,
    fields,
    issueTypeSubtypeQueryIsLoading,
    isTemplateLoading,
    createIssueIsLoading,
    createIssueIsSuccess,
    errors,
    hasErrors,
    cleanErrors,
    shouldShowExtendedFields,
    showControlDeficiencyExtraFields,
    showAuditFindingExtraFields,
    handleCreate,
  } = useCreateIssueForm({
    onCreated,
    onError,
    sourceInfo: sourceInfo && {
      id: sourceInfo.sourceEntityId,
      name: sourceInfo.sourceEntityName,
      entityType: sourceInfo.sourceEntityType,
      link: sourceInfo.sourceEntityLink,
      app: sourceInfo.sourceApp,
    },
    issueType: sourceInfo?.type,
    isSourceError,
    isSourceExpected,
  });

  const handleCancel = useEventCallback(() => {
    setIsCancelling(true);
    if (isSourceExpected && sourceInfo?.callbackUrl) {
      window.location.href = generateCallbackUrl({
        url: sourceInfo.callbackUrl,
        result: 'cancellation',
      });
    } else {
      navigate(`/${paths.issues.master}`);
      setIsCancelling(false);
    }
  });

  useEffect(() => {
    if (isSourceExpected && isSourceError) {
      setError({
        type: APP_ERROR_TYPE.INVALID_SOURCE_PARAMS,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceError]);

  const disableBtns = issueTypeSubtypeQueryIsLoading || isCancelling;
  const isCreating = createIssueIsLoading || createIssueIsSuccess;

  useEffect(() => {
    setHeaderProps({
      viewTitle: {
        title: t('issue:form.new.title'),
        breadcrumbSections: !isSourceExpected
          ? [{ url: '/', title: t('common:appName') }]
          : [],
      },
      buttons: [
        {
          children: t('form.cancel'),
          onClick: handleCancel,
          disabled: disableBtns || isCreating,
          variant: 'outlined',
          'data-testid': 'cancel-button',
        },
        {
          children: isCreating
            ? t('issue:form.new.adding')
            : t('issue:form.new.add'),
          onClick: handleCreate,
          startIcon: <CheckIcon />,
          disabled: disableBtns,
          variant: 'contained',
          'data-testid': 'submit-button',
          loading: isCreating,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableBtns, handleCreate, isCreating, sourceInfo?.callbackUrl]);

  return (
    <>
      {hasErrors && <ErrorFloatingCard onClose={cleanErrors} errors={errors} />}
      <FormProvider {...form}>
        <Box component='form' data-testid='add-issue' px={4} py={5}>
          <IssueFormFields
            fields={fields}
            showSource={isSourceExpected}
            shouldShowExtendedFields={shouldShowExtendedFields}
            showControlDeficiencyExtraFields={showControlDeficiencyExtraFields}
            showAuditFindingExtraFields={showAuditFindingExtraFields}
            isTemplateLoading={isTemplateLoading}
          />
        </Box>
      </FormProvider>
    </>
  );
}
